import { memo, type MouseEvent } from 'react';
import { useStyles } from 'uinix-ui';

import { ActionMode } from '../../types/ActionMode';
import { IconColor } from '../../types/IconColor';
import { SharedButtonProps } from '../../types/SharedButtonProps';
import { StatusType } from '../../types/StatusType';
import { TextColor } from '../../types/TextColor';
import { Badge } from '../Badge/Badge';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';
import { PresetKey } from './types';
import { usePreset } from './usePreset';

interface Props extends SharedButtonProps {
  /** For setting aria-label */
  label?: string;
  preset?: PresetKey;
  /** Optional selected visual state; presets that support it (e.g. `cta-ai`) apply alternate styling */
  isSelected?: boolean;
  PRIVATE_isPadded?: boolean;
  PRIVATE_variant?: 'action' | 'primary' | 'secondary' | 'tertiary' | 'subtle';
}

export const Button = memo((props: Props) => {
  const styles = useStyles();

  const {
    count,
    disabled,
    icon: propIcon,
    iconNode: propIconNode,
    iconPosition: propIconPosition,
    isFullWidth: initialIsFullWidth = false,
    isInline = false,
    isLoading = false,
    isSelected = false,
    PRIVATE_isPadded = true,
    PRIVATE_variant: _PRIVATE_variant,
    label,
    level,
    mode: propMode,
    preset,
    shouldTruncate = false,
    size: propSize,
    status: propStatus,
    text: propText,
    textVariant: propTextVariant,
    theme,
    tooltip,
    type = 'button',
    variant: propVariant,
    onClick,
    ...rest
  } = props;

  const presetResult = usePreset(preset, disabled, isLoading, isSelected);

  const initialIcon = presetResult.icon ?? propIcon;
  const iconPosition = presetResult.iconPosition ?? propIconPosition ?? 'right';
  const size = presetResult.size ?? propSize ?? 'm';
  const status = presetResult.status ?? propStatus;
  const text = presetResult.text ?? propText;
  const textVariant = propTextVariant;
  const initialVariant = presetResult.variant ?? propVariant ?? 'secondary';
  const mode = propMode;

  const isIconMode = mode === 'icon';
  const isAreaMode = mode === 'area';
  const isChipMode = mode === 'chip';
  const isRoundMode = mode === 'round';

  const variant = level ?? (isAreaMode ? 'ghost' : initialVariant); // always override the variant in specific modes
  const isFullWidth = isAreaMode || initialIsFullWidth;

  const isAction = variant === 'action';
  const isDarkTheme = theme === 'dark';
  const isDropdown = preset === 'dropdown';
  const isRightIcon = iconPosition === 'right';
  const isNoop = !onClick && type !== 'submit';

  const aiPresets = preset === 'cta-ai';

  const as = isNoop ? 'div' : 'button';
  const role = isNoop ? 'button' : undefined;
  let icon = initialIcon;
  if (isLoading) {
    icon = aiPresets ? 'loading-dashed' : 'loading';
  }
  const iconNode = isLoading || initialIcon ? undefined : propIconNode;
  const hasIcon = Boolean(icon || iconNode);
  const isSmallSize = size === 's' || size === 'xs';

  const { h, pl, pr, w } = resolveStyleProps({
    hasIcon,
    isAction,
    isPadded: PRIVATE_isPadded,
    isFullWidth,
    isRightIcon,
    mode,
    size,
  });

  const shouldApplyStatusSurfaceStyles = Boolean(status) && !aiPresets;

  const presetLayoutStyles =
    'layoutStyles' in presetResult && presetResult.layoutStyles
      ? presetResult.layoutStyles
      : null;

  const buttonIcon =
    iconNode ??
    (icon && (
      <Icon
        icon={icon}
        color={status ? (`status.${status}` as IconColor) : undefined}
      />
    ));
  const chevronDown = isDropdown && <Icon icon="chevron-down" mr={-1} />;

  const buttonVariantStyle = isDarkTheme
    ? styles.button.dark[variant]
    : styles.button[variant];

  const darkThemeInteractionStyle =
    isDarkTheme && !isAction ? styles.interactionsInverse : undefined;

  const TextComponent = shouldTruncate ? TruncateText : Text;

  let borderRadius: 'unset' | 'pill' | 'round' | undefined = undefined;
  if (isAreaMode) {
    borderRadius = 'unset';
  } else if (isChipMode) {
    borderRadius = 'pill';
  } else if (isRoundMode) {
    borderRadius = 'round';
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick(event);
    }
  };
  let content: 'cta' | StatusType | undefined = undefined;
  if (status) {
    content = status;
  } else if (variant === 'cta') {
    content = 'cta';
  }

  return (
    <Tooltip
      tooltip={tooltip}
      triggerStyle={isFullWidth ? { width: '100%' } : undefined}
    >
      <Layout
        {...rest}
        as={as}
        align="center"
        aria-disabled={isNoop ? disabled || isLoading : undefined}
        aria-label={
          label ?? (isIconMode && typeof text === 'string' ? text : undefined)
        }
        borderRadius={borderRadius}
        data-eds-styles-disable-interactable={
          isAction ||
          (preset === 'cta-ai' && (isSelected || disabled || isLoading))
        }
        disabled={isNoop ? undefined : disabled || isLoading}
        h={h}
        inline={isInline}
        justify={isAreaMode ? 'flex-start' : 'center'}
        maxW={shouldTruncate ? 'button.max-width' : undefined}
        pl={pl}
        pr={pr}
        py={0}
        role={role}
        spacing={1}
        styles={[
          styles.button.base,
          shouldApplyStatusSurfaceStyles ? styles.colors.status : null,
          shouldApplyStatusSurfaceStyles ? null : buttonVariantStyle,
          darkThemeInteractionStyle,
          presetLayoutStyles,
        ]}
        styleProps={{
          status,
          variant,
        }}
        w={w}
        type={type}
        onClick={handleClick}
      >
        {!isRightIcon && buttonIcon}
        {!isIconMode && !isRoundMode && (
          <>
            <TextComponent
              color={
                isChipMode && status
                  ? (`status.${status}` as TextColor)
                  : 'inherit'
              }
              styles={
                preset === 'cta-ai' && isSelected && !isLoading && !disabled
                  ? [
                      {
                        background:
                          'linear-gradient(92.4deg, #815FE2 -33.9%, #4273D9 142%)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      },
                    ]
                  : undefined
              }
              variant={textVariant ?? (isSmallSize ? 'tiny' : 'body-medium')}
            >
              {text}
            </TextComponent>
            {(count || count === 0) && (
              <Badge number={count} size="compact" status={content} />
            )}
          </>
        )}
        {isRightIcon && buttonIcon}
        {isDropdown && chevronDown}
      </Layout>
    </Tooltip>
  );
});

const resolveStyleProps = ({
  hasIcon,
  isAction,
  isFullWidth,
  isPadded,
  isRightIcon,
  mode,
  size,
}: {
  hasIcon: boolean;
  isAction: boolean;
  isFullWidth: boolean;
  isPadded: boolean;
  isRightIcon: boolean;
  mode?: ActionMode;
  size: 'xs' | 's' | 'm' | 'l';
}) => {
  const h = isAction && mode !== 'icon' ? undefined : `height.${size}`;
  let w = isFullWidth ? '100%' : undefined;

  let px;
  switch (size) {
    case 'l': {
      px = [8, 8];
      break;
    }
    case 'xs': {
      px = [2, 2];
      break;
    }
    default: {
      px = [4, 4];
    }
  }

  switch (mode) {
    case 'area': {
      px = [0, 0];
      break;
    }
    case 'icon':
    case 'round': {
      px = [0, 0];
      w = h; // same size as height
      break;
    }
    default: {
      if (hasIcon) {
        if (size === 's') {
          px = isRightIcon ? [3, 2] : [2, 3];
        } else {
          px = isRightIcon ? [4, 3] : [3, 4];
        }
      }
      break;
    }
  }

  const [pl, pr] = isPadded ? px : [0, 0];

  return { h, pl, pr, w };
};
